<template>
  <div class="min-h-screen bg-gray-50 mt-[100px] pb-20">
    <!-- Background decorative elements -->
    <div class="absolute inset-0 overflow-hidden pointer-events-none">
      <div
        class="absolute -top-20 -right-16 sm:-right-32 w-48 sm:w-96 h-48 sm:h-96 bg-gradient-to-br from-blue-200/20 to-indigo-300/10 rounded-full blur-3xl"
      ></div>
      <div
        class="absolute top-1/3 -left-16 sm:-left-32 w-40 sm:w-80 h-40 sm:h-80 bg-gradient-to-tr from-purple-200/20 to-pink-300/10 rounded-full blur-3xl"
      ></div>
    </div>

    <div class="relative container mx-auto px-4 py-8">
      <!-- Page Header - Sans card -->
      <div class="mb-8 text-center">
        <div class="flex items-center justify-start mb-4">
          <div>
            <h1 class="text-4xl font-bold text-left text-gray-900 mb-2">
              Historique des tentatives
            </h1>
            <p class="text-lg text-gray-600">
              Consultez toutes vos tentatives d'exercices et suivez vos progrès
            </p>
          </div>
        </div>
      </div>

      <!-- Filters Section -->
      <div class="mb-8">
        <div
          class="bg-white/90 backdrop-blur-xl border border-gray-200/60 rounded-2xl p-6 shadow-lg"
        >
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <!-- Search Filter -->
            <div class="lg:col-span-2">
              <div class="relative">
                <i
                  class="fa-solid fa-search absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"
                ></i>
                <input
                  v-model="searchQuery"
                  type="text"
                  placeholder="Rechercher un exercice..."
                  class="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50/50"
                />
              </div>
            </div>

            <!-- Status Filter -->
            <div>
              <select
                v-model="selectedStatus"
                class="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50/50"
              >
                <option value="">Tous les statuts</option>
                <option value="completed">Terminé</option>
                <option value="in-progress">En cours</option>
              </select>
            </div>

            <!-- Sort Filter -->
            <div>
              <select
                v-model="sortOrder"
                class="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50/50"
              >
                <option value="desc">Plus récent</option>
                <option value="asc">Plus ancien</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <LoadingState v-if="loading"></LoadingState>

      <!-- Error State -->
      <div v-else-if="error" class="max-w-md mx-auto">
        <div
          class="bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-2xl p-8 shadow-xl text-center"
        >
          <div
            class="w-16 h-16 bg-gradient-to-br from-red-500 to-red-600 rounded-full flex items-center justify-center mx-auto mb-4"
          >
            <i class="fa-solid fa-exclamation-triangle text-white text-2xl"></i>
          </div>
          <h3 class="text-xl font-bold text-red-900 mb-2">Erreur de chargement</h3>
          <p class="text-red-700 mb-6">{{ error }}</p>
          <button
            @click="loadAttempts"
            class="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 hover:scale-105"
          >
            <i class="fa-solid fa-refresh mr-2"></i>
            Réessayer
          </button>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else-if="filteredAttempts.length === 0" class="text-center py-20">
        <div
          class="w-32 h-32 bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl flex items-center justify-center mx-auto mb-6"
        >
          <i class="fa-solid fa-inbox text-gray-400 text-5xl"></i>
        </div>
        <h3 class="text-2xl font-bold text-gray-500 mb-4">Aucune tentative trouvée</h3>
        <p class="text-gray-400 mb-8 text-lg">
          {{
            searchQuery
              ? 'Aucune tentative ne correspond à votre recherche'
              : "Vous n'avez pas encore d'historique d'exercices"
          }}
        </p>
        <button
          v-if="!searchQuery"
          @click="goToExercises"
          class="bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 hover:scale-105 shadow-lg"
        >
          <i class="fa-solid fa-play mr-2"></i>
          Commencer un exercice
        </button>
      </div>

      <!-- Attempts Table -->
      <div
        v-else
        class="bg-white/90 backdrop-blur-xl border border-gray-200/60 rounded-2xl shadow-xl overflow-hidden"
      >
        <!-- Table Header - Desktop -->
        <div
          class="hidden lg:grid lg:grid-cols-12 gap-4 items-center p-6 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 font-semibold text-gray-700"
        >
          <div class="col-span-4">
            <div class="flex items-center">
              <i class="fa-solid fa-file-alt mr-2 text-blue-500"></i>
              Exercice
            </div>
          </div>
          <div class="col-span-2">
            <div class="flex items-center">
              <i class="fa-solid fa-calendar mr-2 text-green-500"></i>
              Date
            </div>
          </div>
          <div class="col-span-1">
            <div class="flex items-center">
              <i class="fa-solid fa-clock mr-2 text-orange-500"></i>
              Durée
            </div>
          </div>
          <div class="col-span-2">
            <div class="flex items-center">
              <i class="fa-solid fa-info-circle mr-2 text-purple-500"></i>
              Statut
            </div>
          </div>
          <div class="col-span-2">
            <div class="flex items-center">
              <i class="fa-solid fa-trophy mr-2 text-yellow-500"></i>
              Score
            </div>
          </div>
          <div class="col-span-1">
            <div class="flex items-center justify-center">
              <i class="fa-solid fa-cog text-gray-400"></i>
            </div>
          </div>
        </div>

        <!-- Table Body -->
        <div class="divide-y divide-gray-100">
          <div
            v-for="(attempt, key) in paginatedAttempts"
            :key="attempt.slug"
            class="group hover:bg-blue-50/30 transition-all duration-300 cursor-pointer"
            @click="viewResult(attempt)"
          >
            <!-- Desktop Layout -->
            <div class="hidden lg:grid lg:grid-cols-12 gap-4 items-center p-6">
              <!-- Exercise Info -->
              <div class="col-span-4">
                <div class="flex items-center space-x-4">
                  <div
                    class="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center flex-shrink-0"
                  >
                    <!-- <i class="fa-solid fa-file-alt text-white"></i> -->
                    <span class="text-white font-bold text-lg">{{ (currentPage - 1) * itemsPerPage + key + 1 }}</span>
                  </div>
                  <div class="min-w-0">
                    <h3
                      class="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors truncate"
                    >
                      {{ getExerciseTitle(attempt) }}
                    </h3>
                    <div class="flex items-center space-x-3 text-sm text-gray-500 mt-1">
                      <span class="flex items-center">
                        <i class="fa-solid fa-tag mr-1"></i>
                        {{ getExerciseType(attempt) }}
                      </span>
                      <span v-if="attempt.exercice?.difficulty_level" class="flex items-center">
                        <i class="fa-solid fa-star mr-1"></i>
                        Niveau {{ attempt.exercice.difficulty_level }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Date -->
              <div class="col-span-2">
                <div class="text-sm font-medium text-gray-700">
                  {{ formatDate(attempt.started_at || attempt.created_at) }}
                </div>
              </div>

              <!-- Duration -->
              <div class="col-span-1">
                <div
                  v-if="attempt.completed_at && attempt.time_spent"
                  class="text-sm font-medium text-gray-700"
                >
                  {{ formatDuration(attempt.time_spent) }}
                </div>
                <div v-else class="text-sm text-gray-400">-</div>
              </div>

              <!-- Status -->
              <div class="col-span-2">
                <div class="flex items-center space-x-2">
                  <div
                    :class="[
                      'px-3 py-1.5 rounded-full text-xs font-semibold flex items-center space-x-1',
                      attempt.completed_at
                        ? 'bg-green-100 text-green-800'
                        : 'bg-orange-100 text-orange-800',
                    ]"
                  >
                    <i
                      :class="
                        attempt.completed_at ? 'fa-solid fa-check-circle' : 'fa-solid fa-clock'
                      "
                    ></i>
                    <span>{{ attempt.completed_at ? 'Terminé' : 'En cours' }}</span>
                  </div>

                  <!-- Progress for incomplete -->
                  <div v-if="!attempt.completed_at" class="flex-1">
                    <div class="w-full bg-gray-200 rounded-full h-2">
                      <div
                        class="bg-gradient-to-r from-orange-400 to-orange-500 h-2 rounded-full transition-all duration-300"
                        :style="{ width: getProgressPercentage(attempt) + '%' }"
                      ></div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Score -->
              <div class="col-span-2">
                <div
                  v-if="attempt.completed_at && attempt.score !== null"
                  class="flex items-center space-x-2"
                >
                  <div
                    :class="[
                      'text-2xl font-bold px-3 py-1 rounded-lg flex items-center space-x-1',
                      'bg-gray-200 text-gray-600',
                    ]"
                  >
                    <span>{{ Math.round(attempt.score) }}</span>
                  </div>
                  <div
                    v-if="
                      attempt.recommendations && Object.keys(attempt.recommendations).length > 0
                    "
                  >
                    <i
                      class="fa-solid fa-lightbulb text-yellow-500"
                      title="Recommandations disponibles"
                    ></i>
                  </div>
                </div>
                <div v-else class="text-sm text-gray-400">-</div>
              </div>

              <!-- Actions -->
              <div class="col-span-1">
                <div
                  class="flex items-center font-bold group-hover:text-blue-500 text-gray-500 justify-center"
                >
                  Details
                  <i
                    class="fa-solid fa-chevron-right text-gray-400 group-hover:text-blue-500 transition-colors"
                  ></i>
                </div>
              </div>
            </div>

            <!-- Mobile Layout -->
            <div class="lg:hidden p-4">
              <div class="flex items-start justify-between mb-3">
                <div class="flex items-start space-x-3 min-w-0 flex-1">
                  <div
                    class="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center flex-shrink-0"
                  >
                    <span class="text-white font-bold text-lg">{{ key + 1 }}</span>
                  </div>
                  <div class="min-w-0 flex-1">
                    <h3
                      class="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors text-sm leading-tight"
                    >
                      {{ getExerciseTitle(attempt) }}
                    </h3>
                    <div class="flex items-center space-x-2 text-xs text-gray-500 mt-1">
                      <span>{{ formatDate(attempt.started_at || attempt.created_at) }}</span>
                      <span>•</span>
                      <span>{{ getExerciseType(attempt) }}</span>
                    </div>
                  </div>
                </div>

                <div class="flex flex-col items-end space-y-1 flex-shrink-0 ml-2">
                  <div
                    :class="[
                      'px-2 py-1 rounded-full text-xs hidden md:block font-semibold',
                      attempt.completed_at
                        ? 'bg-green-100 text-green-800'
                        : 'bg-orange-100 text-orange-800',
                    ]"
                  >
                    {{ attempt.completed_at ? 'Terminé' : 'En cours' }}
                  </div>
                  <div v-if="attempt.completed_at && attempt.score !== null">
                    <span
                      :class="['text-lg font-bold px-2 py-1 rounded', 'bg-gray-300 text-gray-600']"
                    >
                      {{ Math.round(attempt.score) }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Mobile Progress Bar -->
              <div v-if="!attempt.completed_at" class="mb-2">
                <div class="flex items-center justify-between text-xs text-gray-500 mb-1">
                  <span>Progression</span>
                  <span>{{ getProgressPercentage(attempt) }}%</span>
                </div>
                <div class="w-full bg-gray-200 rounded-full h-2">
                  <div
                    class="bg-gradient-to-r from-orange-400 to-orange-500 h-2 rounded-full transition-all duration-300"
                    :style="{ width: getProgressPercentage(attempt) + '%' }"
                  ></div>
                </div>
              </div>

              <!-- Mobile Additional Info -->
              <div class="flex items-center justify-between text-xs text-gray-500 mt-2">
                <div class="flex items-center space-x-3">
                  <span v-if="attempt.completed_at && attempt.time_spent" class="flex items-center">
                    <i class="fa-solid fa-clock mr-1"></i>
                    {{ formatDuration(attempt.time_spent) }}
                  </span>
                  <span v-if="attempt.exercice?.difficulty_level" class="flex items-center">
                    <i class="fa-solid fa-star mr-1"></i>
                    Niveau {{ attempt.exercice.difficulty_level }}
                  </span>
                </div>
                <div class="flex items-center space-x-2">
                  <i
                    v-if="
                      attempt.recommendations && Object.keys(attempt.recommendations).length > 0
                    "
                    class="fa-solid fa-lightbulb text-yellow-500"
                    title="Recommandations disponibles"
                  ></i>
                  <i
                    class="fa-solid fa-chevron-right text-gray-400 group-hover:text-blue-500 transition-colors"
                  ></i>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="totalPages > 1" class="px-6 py-4 bg-gray-50/50 border-t border-gray-200">
          <div class="flex items-center justify-between">
            <div class="flex items-center space-x-2">
              <span class="text-sm text-gray-700">
                Affichage de {{ (currentPage - 1) * itemsPerPage + 1 }} à
                {{ Math.min(currentPage * itemsPerPage, filteredAttempts.length) }}
                sur {{ filteredAttempts.length }} tentatives
              </span>
            </div>

            <div class="flex items-center space-x-2">
              <button
                @click="previousPage"
                :disabled="currentPage === 1"
                :class="[
                  'px-4 py-2 rounded-lg font-semibold transition-all duration-300 flex items-center space-x-1',
                  currentPage === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300 hover:scale-105',
                ]"
              >
                <i class="fa-solid fa-chevron-left"></i>
                <span class="hidden sm:inline">Précédent</span>
              </button>

              <div class="flex items-center space-x-1">
                <button
                  v-for="page in visiblePages"
                  :key="page"
                  @click="goToPage(page)"
                  :class="[
                    'w-10 h-10 rounded-lg font-semibold transition-all duration-300',
                    page === currentPage
                      ? 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white shadow-lg scale-110'
                      : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:scale-105',
                  ]"
                >
                  {{ page }}
                </button>
              </div>

              <button
                @click="nextPage"
                :disabled="currentPage === totalPages"
                :class="[
                  'px-4 py-2 rounded-lg font-semibold transition-all duration-300 flex items-center space-x-1',
                  currentPage === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300 hover:scale-105',
                ]"
              >
                <span class="hidden sm:inline">Suivant</span>
                <i class="fa-solid fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useToast } from '@/composables/useToast'
import apiClient from '@/api/axios'
import LoadingState from '@/components/exercices/LoadingState.vue'

const router = useRouter()
const authStore = useAuthStore()
const { success: showSuccess, error: showError } = useToast()

// State
const attempts = ref([])
const loading = ref(false)
const error = ref(null)
const searchQuery = ref('')
const selectedStatus = ref('')
const sortOrder = ref('desc')
const currentPage = ref(1)
const itemsPerPage = 10

// No need for separate exercises since they're embedded in attempts

// Computed
const filteredAttempts = computed(() => {
  let filtered = [...attempts.value]

  // Filter by search query
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    filtered = filtered.filter(
      (attempt) =>
        getExerciseTitle(attempt).toLowerCase().includes(query) ||
        getExerciseType(attempt).toLowerCase().includes(query),
    )
  }

  // Filter by status
  if (selectedStatus.value) {
    filtered = filtered.filter((attempt) => {
      if (selectedStatus.value === 'completed') return attempt.completed_at
      if (selectedStatus.value === 'in-progress') return !attempt.completed_at
      return true
    })
  }

  // Sort by date
  filtered.sort((a, b) => {
    const dateA = new Date(a.created_at || a.date)
    const dateB = new Date(b.created_at || b.date)
    return sortOrder.value === 'desc' ? dateB - dateA : dateA - dateB
  })

  return filtered
})

const totalPages = computed(() => Math.ceil(filteredAttempts.value.length / itemsPerPage))

const paginatedAttempts = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage
  const end = start + itemsPerPage
  return filteredAttempts.value.slice(start, end)
})

const visiblePages = computed(() => {
  const pages = []
  const maxVisible = 5
  let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2))
  let end = Math.min(totalPages.value, start + maxVisible - 1)

  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1)
  }

  for (let i = start; i <= end; i++) {
    pages.push(i)
  }

  return pages
})

// Methods
const loadAttempts = async () => {
  loading.value = true
  error.value = null

  try {
    // Check if user is authenticated
    if (!authStore.token) {
      throw new Error("Token d'authentification manquant")
    }

    // Fetch exercise attempts using apiClient
    const response = await apiClient.get('/exercice-attempt/')

    // apiClient already handles JSON parsing and errors
    attempts.value = Array.isArray(response.data) ? response.data : []
  } catch (err) {
    console.error('Load attempts error:', err)
    error.value = err.message || 'Une erreur est survenue lors du chargement'
    showError("Impossible de charger l'historique")
  } finally {
    loading.value = false
  }
}

const loadExercises = async () => {
  // Not needed since exercise data is embedded in attempts
  return
}

const getExerciseTitle = (attempt) => {
  // Exercise data is embedded in the attempt object
  return attempt.exercice?.title || `Exercice ${attempt.slug}`
}

const getExerciseType = (attempt) => {
  return attempt.exercice?.type_exam || 'Exercice'
}

const getScoreClass = (score) => {
  if (score >= 80) return 'bg-green-100 text-green-800'
  if (score >= 60) return 'bg-yellow-100 text-yellow-800'
  return 'bg-red-100 text-red-800'
}

const formatDate = (dateString) => {
  if (!dateString) return 'Date inconnue'

  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const formatDuration = (timeSpent) => {
  if (!timeSpent) return 'N/A'

  const minutes = Math.floor(timeSpent / 60)
  const seconds = timeSpent % 60

  if (minutes > 0) {
    return `${minutes}min ${seconds}s`
  }
  return `${seconds}s`
}

const getProgressPercentage = (attempt) => {
  // For exercice-attempt, we can use the status field or calculate based on completion
  if (attempt.completed_at) return 100
  if (attempt.status) return Math.min(attempt.status * 20, 90) // Convert status to percentage
  if (attempt.started_at && !attempt.completed_at) return 25 // Started but not completed
  return 10 // Just created
}

const viewResult = (attempt) => {
  if (attempt.completed_at) {
    // Navigate to result page using the attempt slug
    router.push(`/exercice-results/${attempt.slug}`)
  } else {
    // Navigate to continue the exercise using the exercice slug
    router.push(`/exercice/${attempt.exercice?.slug || attempt.exercice}`)
  }
}

const goToExercises = () => {
  router.push('/dashboard')
}

const previousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--
  }
}

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++
  }
}

const goToPage = (page) => {
  currentPage.value = page
}

// Watch for filter changes to reset pagination
watch([searchQuery, selectedStatus, sortOrder], () => {
  currentPage.value = 1
})

// Lifecycle
onMounted(() => {
  if (authStore.isAuthenticated) {
    loadAttempts()
  } else {
    router.push('/login')
  }
})
</script>

<style scoped>
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
